import { DATA_SOURCE_WORKSHEET_NAME } from "../../globalValues";
import { EditedData } from "../../producerServices/processDataProducerEvent/utils/validateAndSetYieldValue";
import { SecuritiesInStagingState, SecuritiesValuesState } from "../../store/types";
import {
  MEMORY_COLS,
  MEMORY_TABLE_NAME,
  MEMORY_TABLE_NAME_COL_IDX,
  MEMORY_TABLE_ROWS_IDX,
  MEMORY_TABLE_VALUE_COL_IDX,
  MemoryTableColsNames,
} from "./config";

export const createMemoryTable = async (context: Excel.RequestContext) => {
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);
  let table = sheet.tables.getItemOrNullObject(MEMORY_TABLE_NAME);
  await context.sync();

  if (!table.isNullObject) {
    return table;
  }

  table = sheet.tables.add(
    `${MEMORY_COLS[MEMORY_TABLE_NAME_COL_IDX]}1:${MEMORY_COLS[MEMORY_TABLE_VALUE_COL_IDX]}1`,
    true /*hasHeaders*/
  );
  table.name = MEMORY_TABLE_NAME;
  table.getHeaderRowRange().values = [["Name", "Value"]];

  table.getRange().format.autofitColumns();
  table.getRange().format.autofitRows();

  table.rows.add(undefined, [[MemoryTableColsNames.STAGING_VALUES, "{}"]]);
  table.rows.add(undefined, [[MemoryTableColsNames.SAVED_VALUES, "{}"]]);
  table.rows.add(undefined, [[MemoryTableColsNames.AUTO_UPDATE, "false"]]);

  sheet.getRange(MEMORY_COLS[MEMORY_TABLE_NAME_COL_IDX] + ":" + MEMORY_COLS[MEMORY_TABLE_VALUE_COL_IDX]).columnHidden =
    true;

  await context.sync();
  return table;
};

const saveInMemory = async ({
  context,
  data,
}: {
  context: Excel.RequestContext;
  data: { name: MemoryTableColsNames; value: string };
}) => {
  const fieldName = data.name;
  const fieldValue = data.value;
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);

  let table = sheet.tables.getItemOrNullObject(MEMORY_TABLE_NAME);
  await context.sync();

  if (table.isNullObject) {
    table = await createMemoryTable(context);
  }
  table.rows.getItemAt(MEMORY_TABLE_ROWS_IDX[fieldName]).getRange().values = [[fieldName, fieldValue]];
  return await context.sync();
};

const getFromMemory = async (context: Excel.RequestContext, fieldName: MemoryTableColsNames) => {
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);

  let table = sheet.tables.getItemOrNullObject(MEMORY_TABLE_NAME);
  await context.sync();

  if (table.isNullObject) {
    return null;
  }

  const rowData = table.rows.getItemAt(MEMORY_TABLE_ROWS_IDX[fieldName]).getRange();
  rowData.load("values");
  await context.sync();

  return rowData.values[0][1];
};

export const saveStagingStateInMemoryTable = async (
  context: Excel.RequestContext,
  stagingState: SecuritiesInStagingState
) => {
  await saveInMemory({
    context,
    data: { name: MemoryTableColsNames.STAGING_VALUES, value: JSON.stringify(stagingState) },
  });
  await context.sync();
};

export const getStagingStateFromMemoryTable = async (context: Excel.RequestContext) => {
  const stagingState = await getFromMemory(context, MemoryTableColsNames.STAGING_VALUES);
  await context.sync();
  return stagingState;
};

export const saveDataInMemoryTable = async (context: Excel.RequestContext, data: SecuritiesValuesState) => {
  await saveInMemory({ context, data: { name: MemoryTableColsNames.SAVED_VALUES, value: JSON.stringify(data) } });
  await context.sync();
};

export const getDataFromMemoryTable = async (context: Excel.RequestContext) => {
  const data = await getFromMemory(context, MemoryTableColsNames.SAVED_VALUES);
  await context.sync();
  return JSON.parse(data) as { [key: string]: EditedData };
};

export const saveAutoUpdateInMemoryTable = async (context: Excel.RequestContext, autoUpdate: boolean) => {
  await saveInMemory({ context, data: { name: MemoryTableColsNames.AUTO_UPDATE, value: JSON.stringify(autoUpdate) } });
  await context.sync();
};

export const getAutoUpdateFromMemoryTable = async (context: Excel.RequestContext) => {
  const autoUpdate = await getFromMemory(context, MemoryTableColsNames.AUTO_UPDATE);
  await context.sync();
  return autoUpdate;
};
